import { DELIVERY_PROVIDER_STATUS, type DeliveryAddress } from '@koala/sdk';
import {
  type IProgressBarStatus,
  PROGRESS_BAR_STATES,
} from '@/components/orderDetail/deliveryStatus/progressBar/constants';
import { type IDeliveryStatusState } from '@/types/deliveryStatus';
import { type IConveyanceDeliveryAddress } from '@/types/fulfillment';

export const isDeliveryStatusActive = (deliveryStatus: IDeliveryStatusState): boolean =>
  [
    DELIVERY_PROVIDER_STATUS.PENDING,
    DELIVERY_PROVIDER_STATUS.PICKUP_IN_PROGRESS,
    DELIVERY_PROVIDER_STATUS.DELIVERY_IN_PROGRESS,
  ].includes(deliveryStatus?.status);

export const isDeliveryStatusTerminated = (deliveryStatus: IDeliveryStatusState): boolean =>
  [
    DELIVERY_PROVIDER_STATUS.CANCELLED,
    DELIVERY_PROVIDER_STATUS.RETURNED,
    DELIVERY_PROVIDER_STATUS.DELIVERED,
  ].includes(deliveryStatus?.status);

export const isDeliveryStatusCanceledOrReturned = (deliveryStatus: IDeliveryStatusState): boolean =>
  [DELIVERY_PROVIDER_STATUS.CANCELLED, DELIVERY_PROVIDER_STATUS.RETURNED].includes(
    deliveryStatus?.status,
  );

export const setProgressBarStates = (
  currentStates: IProgressBarStatus[],
  deliveryStatus: IDeliveryStatusState,
) => {
  const receivedDeliveryStatus: DELIVERY_PROVIDER_STATUS = deliveryStatus.status;
  const receivedStatusPosition = currentStates.find(isSameStatus(receivedDeliveryStatus))?.position;

  currentStates.forEach(
    /** @TODO determine if receivedStatusPosition is actually nullable.  */
    // @ts-expect-error
    setStateForStatus(receivedStatusPosition, receivedDeliveryStatus),
  );
};

const isSameStatus =
  (receivedStatus: DELIVERY_PROVIDER_STATUS) => (progressBarStatus: IProgressBarStatus) =>
    progressBarStatus.status === receivedStatus;

export const sortByPosition = (a: IProgressBarStatus, b: IProgressBarStatus): number =>
  a.position - b.position;

const setStateForStatus =
  (receivedStatusPosition: number, receivedStatus: DELIVERY_PROVIDER_STATUS) =>
  (currentProgressBarStatus: IProgressBarStatus) => {
    if (
      currentProgressBarStatus.position < receivedStatusPosition &&
      currentProgressBarStatus.state !== PROGRESS_BAR_STATES.FINISHED
    ) {
      currentProgressBarStatus.state = PROGRESS_BAR_STATES.FINISHED;
    }

    if (currentProgressBarStatus.status === receivedStatus) {
      currentProgressBarStatus.state = PROGRESS_BAR_STATES.CURRENT_IN_PROGRESS;
    }
  };

/**
 * Convert app type IConveyanceDeliveryAddress to sdk type DeliveryAddress
 * Not needed once the types are reconciled throughout the app.
 */
export const convertConveyanceToDeliveryAddress = (
  deliveryAddress: IConveyanceDeliveryAddress,
): DeliveryAddress => {
  return {
    id: deliveryAddress.id ?? null,
    stale_id: deliveryAddress.stale_id ?? undefined,
    day_wanted: deliveryAddress.day_wanted,
    time_wanted: deliveryAddress.time_wanted,
    // only needed for type
    time_wanted_mode: 'advance',
    state: deliveryAddress.state,
    city: deliveryAddress.city,
    street_address: deliveryAddress.street_address,
    street_address_2: deliveryAddress.street_address_2 ?? undefined,
    zip_code: deliveryAddress.zip_code,
    zip: deliveryAddress.zip_code,
    phone_number: deliveryAddress.phone_number ?? undefined,
    special_instructions: deliveryAddress.special_instructions ?? undefined,
    default: deliveryAddress.default ?? false,
  };
};
